import React from 'react';
import { Save } from 'lucide-react';

interface SecuritySettings {
  twoFactorAuth: boolean;
  sessionTimeout: string;
  apiAccess: boolean;
}

interface SecurityTabProps {
  security: SecuritySettings;
  setSecurity: (data: SecuritySettings) => void;
  onSave: () => Promise<void>;
  saving?: boolean;
}

const SecurityTab: React.FC<SecurityTabProps> = ({
  security,
  setSecurity,
  onSave,
  saving = false,
}) => {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-4">
          Security Settings
        </h3>
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-slate-900 dark:text-slate-100">
                Two-Factor Authentication
              </h4>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Add an extra layer of security to your account
              </p>
            </div>
            <input
              type="checkbox"
              checked={security.twoFactorAuth}
              onChange={(e) => setSecurity({ ...security, twoFactorAuth: e.target.checked })}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-slate-300 dark:border-slate-600 rounded"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Session Timeout (minutes)
            </label>
            <select
              value={security.sessionTimeout}
              onChange={(e) => setSecurity({ ...security, sessionTimeout: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="15">15 minutes</option>
              <option value="30">30 minutes</option>
              <option value="60">1 hour</option>
              <option value="240">4 hours</option>
              <option value="480">8 hours</option>
            </select>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-slate-900 dark:text-slate-100">
                API Access
              </h4>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Allow API access for this account
              </p>
            </div>
            <input
              type="checkbox"
              checked={security.apiAccess}
              onChange={(e) => setSecurity({ ...security, apiAccess: e.target.checked })}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-slate-300 dark:border-slate-600 rounded"
            />
          </div>
        </div>
      </div>
      <div className="flex justify-end">
        <button
          onClick={onSave}
          disabled={saving}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Security Settings'}
        </button>
      </div>
    </div>
  );
};

export default SecurityTab;